import { CTACard } from '../components/CTACard';
import { ProgressAccordionStepProps, AccordionStep } from '../components/AccordionStep';
import { ProgressStepStatusEnum, SimplifiedUserRole } from '@platform/client/schemas';
import { Link } from 'wouter';
import cx from 'classix';
import { HubExternalLink, ResearchPanelLink } from '../components/HubExternalLink';

export const Reflect = ({
  status,
  isTeam,
  isOpen,
  onToggle,
  userType,
  surveyUrl,
  storyUrl,
}: ProgressAccordionStepProps & { surveyUrl?: string | null; storyUrl?: string | null }) => {
  const disableLinksButtons =
    status !== ProgressStepStatusEnum.active_in_progress &&
    status !== ProgressStepStatusEnum.success;
  return (
    <AccordionStep title='Reflect' status={status} finalStep isOpen={isOpen} onToggle={onToggle}>
      <div className='flex flex-col gap-4'>
        {userType === SimplifiedUserRole.PROFESSIONAL && <ProfessionalReflectHeader />}
        {userType === SimplifiedUserRole.REPRESENTATIVE && (
          <OrganizationReflectHeader isTeam={isTeam} />
        )}
        <CTACard
          title='Complete your wrap-up survey'
          description="It only takes about 10 minutes, and it's the final step to mark your project as complete."
          ctaButtonProps={{
            size: 'sm',
            children: 'Complete wrap up questions',
            variant: 'primary',
            // Button component when used as link will appear active if href is provided even if isDisabled is true.
            href:
              status !== ProgressStepStatusEnum.active_in_progress ?
                undefined
              : (surveyUrl ?? undefined),
            isDisabled: status !== ProgressStepStatusEnum.active_in_progress,
            target: '_blank',
          }}
        />
        <p className='text-md font-semibold'>Other things you can do</p>
        <ul
          className={cx(
            'list-disc px-4',
            status === ProgressStepStatusEnum.active_in_progress && 'blue-links',
          )}
        >
          <li>
            <HubExternalLink url={storyUrl} active={!disableLinksButtons}>
              Write a testimonial
            </HubExternalLink>
            &nbsp;on your experience that may be featured on our website
          </li>
          {userType === SimplifiedUserRole.REPRESENTATIVE && (
            <li>
              Need more support?&nbsp;
              {!disableLinksButtons ?
                <Link className='blue-link' href='/create-project'>
                  Post another project
                </Link>
              : <>Post another project</>}
              &nbsp;to get help implementing your new deliverables or taking the next step in your
              organization's growth.
            </li>
          )}
          <li>
            <ResearchPanelLink userType={userType} active={!disableLinksButtons}>
              Join our research panel
            </ResearchPanelLink>
            &nbsp;to provide feedback that will help us improve the platform
          </li>
        </ul>
      </div>
    </AccordionStep>
  );
};

const OrganizationReflectHeader = ({ isTeam }: { isTeam?: boolean }) => {
  return (
    <>
      <p className='text-md font-semibold'>You did it! Congratulations 🎉🥳</p>
      <p>
        {`Your organization just completed a major milestone! Thank you for the time, energy, and
        openness you brought to this collaboration. Your efforts helped make a real difference —
        both for your team and the ${isTeam ? 'professionals' : 'professional'} who worked alongside you.`}
      </p>
      <p>
        This experience isn't just about the deliverables achieved; it's also about what your
        organization learned in the process. Take a few minutes to reflect on the results and share
        your insights so we can celebrate your impact and continue improving the experience for
        future impact enterprises
      </p>
    </>
  );
};

const ProfessionalReflectHeader = () => {
  return (
    <>
      <p className='text-md font-semibold'>
        Many people want to create impact. You actually did. Congratulations 🎉🥳
      </p>
      <p>
        You brought your time, skills, and courage to something that may have felt like a stretch,
        and it made a real difference.
      </p>
      <p>
        But this experience isn't just about the impact you created for the organization, it's also
        about the impact it had on you. Take a few minutes to reflect on what you achieved together.
        Your insights help us celebrate your impact and improve future experiences for others.
      </p>
    </>
  );
};
